/**
 * Hooks del directorio — TanStack React Query para cache y estados de carga.
 * 
 * Estos hooks reemplazan el uso directo de mocks en los componentes.
 * Proveen: data, isLoading, error con cache automático.
 * 
 * Implementa: S1-02, S1-03, S1-06 del backlog
 */

import { useQuery } from '@tanstack/react-query'
import {
  fetchDoctors,
  fetchDoctorDetail,
  fetchDoctorBookingReady,
  fetchSpecialties,
  fetchDepartments,
  fetchMunicipalities,
  fetchDirectoryCountries,
  fetchDirectoryTerritoryUnits,
  fetchDirectoryTerritoryScope,
} from '../services/directory.service'
import type { DirectoryFilters } from '../types/directory.types'

// ─────────────────────────────────────────────
// LISTADO DE MÉDICOS
// ─────────────────────────────────────────────

/**
 * Hook para obtener la lista de médicos del directorio con filtros.
 * 
 * Uso en page.tsx del Home:
 *   const { data: doctors, isLoading, error } = useDoctors(filters)
 */
export function useDoctors(filters: DirectoryFilters) {
  return useQuery({
    queryKey: ['doctors', filters],
    queryFn: () => fetchDoctors(filters),
    // Dos condiciones, ambas fail closed:
    //  · país de contexto válido (F3E-2). Deshabilitada la query queda en
    //    `isPending`: el llamador no debe leer `isLoading` para decidir el
    //    skeleton, o mostraría «0 resultados» mientras llega el país;
    //  · si hay unidad territorial elegida, su scope debe estar resuelto
    //    (F3E-3B). Mientras no lo esté —o si su RPC falló— no se consulta.
    enabled:
      filters.countryId != null &&
      !(filters.territoryUnitId != null && filters.territoryUnitIds == null),
    staleTime: 1000 * 60 * 2, // 2 min cache — los médicos no cambian cada segundo
    // Mantener los datos previos evita el parpadeo al teclear una búsqueda o
    // cambiar de especialidad. Pero NO debe cruzar un cambio de país ni de
    // ubicación: se verían los médicos del territorio anterior bajo el nombre del
    // nuevo. Ante un cambio geográfico se devuelve undefined → skeleton.
    placeholderData: (previousData, previousQuery) => {
      const previo = previousQuery?.queryKey?.[1] as DirectoryFilters | undefined
      if (!previo) return previousData
      if (previo.countryId !== filters.countryId) return undefined
      if (previo.territoryUnitId !== filters.territoryUnitId) return undefined
      return previousData
    },
  })
}

// ─────────────────────────────────────────────
// DETALLE DE UN DOCTOR
// ─────────────────────────────────────────────

/**
 * Hook para obtener el detalle completo de un doctor.
 * 
 * Uso en DoctorDetail page:
 *   const { data: doctor, isLoading, error } = useDoctorDetail(doctorId)
 */
export function useDoctorDetail(doctorId: string | undefined) {
  return useQuery({
    queryKey: ['doctor', doctorId],
    queryFn: () => fetchDoctorDetail(doctorId!),
    enabled: !!doctorId, // No ejecutar si no hay ID
    staleTime: 1000 * 60 * 5, // 5 min cache — el perfil no cambia frecuentemente
  })
}

// ─────────────────────────────────────────────
// CATÁLOGOS PARA FILTROS
// ─────────────────────────────────────────────

/**
 * Países habilitados para el directorio (F3E-2). Un request pequeño por sesión:
 * solo cambia cuando el owner habilita un país.
 */
export function useDirectoryCountries() {
  return useQuery({
    queryKey: ['directory-countries'],
    queryFn: fetchDirectoryCountries,
    staleTime: 1000 * 60 * 60, // 1 hora
  })
}

/**
 * Unidades territoriales de un nivel (F3E-3B). Una clave por (país, padre), así
 * que volver atrás en la navegación reutiliza la cache y no repite el request.
 *
 * `enabled` solo con ISO: sin país de contexto no se navega territorio. La clave
 * usa el ISO —identidad externa y estable— y no el id interno del país.
 */
export function useDirectoryTerritoryUnits(
  countryIso: string | null,
  parentId: number | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['directory-territory-units', countryIso, parentId],
    queryFn: () => fetchDirectoryTerritoryUnits(countryIso!, parentId),
    enabled: enabled && !!countryIso,
    staleTime: 1000 * 60 * 60, // 1 hora — mismo criterio que el resto del catálogo GEO
  })
}

/**
 * Alcance territorial de la unidad aplicada (F3E-3B). Una clave por (país,
 * unidad): volver a una ubicación ya visitada no repite la RPC.
 *
 * `retry: false` a propósito, igual que `useDoctorBookingReady`: ante un fallo
 * queremos decidir YA en fail closed y mostrar el error, no dejar el directorio
 * en un limbo mientras se reintenta.
 */
export function useDirectoryTerritoryScope(
  countryIso: string | null,
  unitId: number | null,
) {
  return useQuery({
    queryKey: ['directory-territory-scope', countryIso, unitId],
    queryFn: () => fetchDirectoryTerritoryScope(countryIso!, unitId!),
    enabled: !!countryIso && unitId != null,
    retry: false,
    staleTime: 1000 * 60 * 60, // 1 hora
  })
}

/** Hook para las especialidades (dropdown del filtro) */
export function useSpecialties() {
  return useQuery({
    queryKey: ['specialties'],
    queryFn: fetchSpecialties,
    staleTime: 1000 * 60 * 30, // 30 min — las especialidades casi no cambian
  })
}

/** Hook para departamentos (dropdown del filtro) */
export function useDepartments() {
  return useQuery({
    queryKey: ['departments'],
    queryFn: fetchDepartments,
    staleTime: 1000 * 60 * 60, // 1 hora — los departamentos de SV no cambian
  })
}

/** Hook para municipios, filtrados por departamento si se selecciona uno */
export function useMunicipalities(departmentId: string | null) {
  return useQuery({
    queryKey: ['municipalities', departmentId],
    queryFn: () => fetchMunicipalities(departmentId),
    staleTime: 1000 * 60 * 60, // 1 hora
  })
}

/**
 * Reservabilidad canónica del médico. Se resuelve por `doctor_id`, así que
 * depende del detalle ya cargado (la ruta puede venir por slug).
 *
 * `retry: false` a propósito: ante un fallo queremos decidir YA en fail closed,
 * no reintentar mientras el CTA queda en un limbo visible.
 */
export function useDoctorBookingReady(doctorId: string | undefined) {
  return useQuery({
    queryKey: ['doctor-booking-ready', doctorId],
    queryFn: () => fetchDoctorBookingReady(doctorId!),
    enabled: !!doctorId,
    retry: false,
    staleTime: 1000 * 60,
  })
}
